import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '../api';
import { useAuth } from '../context/AuthContext';

const ADMIN_EMAIL = 'jrbroseph@icloud.com';
const S = {
  page: { minHeight: '100vh', background: '#111318', padding: 24, fontFamily: 'Inter, system-ui, sans-serif' },
  card: { background: '#1c1e26', border: '0.5px solid #2a2d3a', borderRadius: 12, padding: '14px 16px', marginBottom: 0 },
  text: { color: '#e8e6f0' },
  text2: { color: '#8b8a97' },
  text3: { color: '#555' },
  badge: (plan) => ({ padding: '2px 8px', borderRadius: 6, fontSize: 11, fontWeight: 600, background: plan === 'Pro+' ? '#1a3a2a' : plan === 'Pro' ? '#1e1b3a' : '#1a2a3a', color: plan === 'Pro+' ? '#4ade80' : plan === 'Pro' ? '#a78bfa' : '#60a5fa', cursor: 'pointer' }),
  btn: (color) => ({ padding: '3px 8px', borderRadius: 5, border: `0.5px solid ${color}`, background: 'none', fontSize: 10, cursor: 'pointer', color }),
  input: { padding: '6px 12px', borderRadius: 7, border: '0.5px solid #2a2d3a', background: '#13151c', fontSize: 12, color: '#e8e6f0', width: 260 },
  th: { padding: '9px 14px', textAlign: 'left', fontSize: 11, fontWeight: 500, color: '#8b8a97', whiteSpace: 'nowrap', borderBottom: '0.5px solid #2a2d3a', background: '#161820' },
  td: { padding: '10px 14px', borderBottom: '0.5px solid #1e2028' },
};

export default function Admin() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [subscribers, setSubscribers] = useState([]);
  const [editingId, setEditingId] = useState(null);
  const [editPlan, setEditPlan] = useState('');
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (user?.email !== ADMIN_EMAIL) { navigate('/dashboard'); return; }
    Promise.all([
      api.getAdminStats().then(setStats),
      api.getAdminSubscribers().then(setSubscribers),
    ]).finally(() => setLoading(false));
  }, []);

  if (user?.email !== ADMIN_EMAIL) return null;

  const handlePlanChange = async (id) => {
    try {
      await api.updateSubscriber(id, { plan: editPlan });
      setSubscribers(s => s.map(u => u.id === id ? { ...u, plan: editPlan } : u));
      setEditingId(null);
      api.getAdminStats().then(setStats);
    } catch (err) { alert(err.message); }
  };

  const handleExtendTrial = async (id) => {
    const newDate = new Date(Date.now() + 14 * 24 * 60 * 60 * 1000).toISOString();
    try {
      await api.updateSubscriber(id, { trial_ends_at: newDate });
      setSubscribers(s => s.map(u => u.id === id ? { ...u, trial_ends_at: newDate } : u));
    } catch (err) { alert(err.message); }
  };

  const handleDelete = async (id, name) => {
    if (!window.confirm(`Delete ${name}'s account? This cannot be undone.`)) return;
    try {
      await api.deleteSubscriber(id);
      setSubscribers(s => s.filter(u => u.id !== id));
      api.getAdminStats().then(setStats);
    } catch (err) { alert(err.message); }
  };

  const filtered = subscribers.filter(u =>
    u.name?.toLowerCase().includes(search.toLowerCase()) ||
    u.email?.toLowerCase().includes(search.toLowerCase()) ||
    u.agency?.toLowerCase().includes(search.toLowerCase())
  );

  return (
    <div style={S.page}>
      <div style={{ maxWidth: 1200, margin: '0 auto' }}>

        <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 24 }}>
          <button onClick={() => navigate('/dashboard')} style={{ ...S.btn('#8b8a97'), padding: '6px 12px', fontSize: 13 }}>← Back to FLO</button>
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: 22, fontWeight: 700, ...S.text }}>FLO Admin Panel</div>
            <div style={{ fontSize: 12, ...S.text2 }}>Platform management — admin only</div>
          </div>
          <button onClick={() => { api.getAdminStats().then(setStats); api.getAdminSubscribers().then(setSubscribers); }} style={{ ...S.btn('#4ade80'), padding: '6px 12px', fontSize: 12 }}>↻ Refresh</button>
        </div>

        {loading && <div style={{ textAlign: 'center', color: '#555', padding: 40 }}>Loading...</div>}

        {stats && (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(130px, 1fr))', gap: 10, marginBottom: 20 }}>
            {[
              { label: 'Subscribers', value: stats.total_subscribers, color: '#a78bfa' },
              { label: 'MRR', value: '$' + stats.mrr.toLocaleString(), color: '#4ade80' },
              { label: 'Basic', value: stats.by_plan.Basic, color: '#60a5fa' },
              { label: 'Pro', value: stats.by_plan.Pro, color: '#a78bfa' },
              { label: 'Pro+', value: stats.by_plan['Pro+'], color: '#4ade80' },
              { label: 'Total leads', value: stats.total_leads, color: '#fbbf24' },
              { label: 'AI messages', value: stats.ai_messages, color: '#c084fc' },
              { label: 'Appointments', value: stats.total_appointments, color: '#4ade80' },
            ].map(s => (
              <div key={s.label} style={{ background: '#1c1e26', border: '0.5px solid #2a2d3a', borderRadius: 10, padding: '12px 14px' }}>
                <div style={{ fontSize: 11, color: '#8b8a97', marginBottom: 6 }}>{s.label}</div>
                <div style={{ fontSize: 22, fontWeight: 700, color: s.color }}>{s.value}</div>
              </div>
            ))}
          </div>
        )}

        <div style={{ background: '#1c1e26', border: '0.5px solid #2a2d3a', borderRadius: 12, overflow: 'hidden' }}>
          <div style={{ padding: '14px 18px', borderBottom: '0.5px solid #2a2d3a', display: 'flex', alignItems: 'center', gap: 12 }}>
            <span style={{ fontSize: 14, fontWeight: 600, ...S.text, flex: 1 }}>Subscribers ({subscribers.length})</span>
            <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search name, email, agency..." style={S.input} />
          </div>
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
              <thead>
                <tr>
                  {['Name', 'Email', 'Agency', 'Plan', 'Leads', 'Msgs', 'Appts', 'Signed up', 'Trial ends', 'Actions'].map(h => (
                    <th key={h} style={S.th}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {filtered.map(u => {
                  const trialEnd = u.trial_ends_at ? new Date(u.trial_ends_at) : null;
                  const trialExpired = trialEnd && trialEnd < new Date();
                  return (
                    <tr key={u.id} onMouseEnter={e => e.currentTarget.style.background = '#161820'} onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
                      <td style={{ ...S.td, fontWeight: 500, ...S.text }}>{u.name}</td>
                      <td style={{ ...S.td, ...S.text2, fontSize: 12 }}>{u.email}</td>
                      <td style={{ ...S.td, ...S.text2, fontSize: 12 }}>{u.agency || '—'}</td>
                      <td style={S.td}>
                        {editingId === u.id ? (
                          <div style={{ display: 'flex', gap: 4 }}>
                            <select value={editPlan} onChange={e => setEditPlan(e.target.value)} style={{ ...S.input, width: 80, padding: '3px 6px' }}>
                              <option>Basic</option><option>Pro</option><option>Pro+</option>
                            </select>
                            <button onClick={() => handlePlanChange(u.id)} style={{ padding: '3px 7px', borderRadius: 5, border: 'none', background: '#534AB7', color: '#fff', fontSize: 10, cursor: 'pointer' }}>✓</button>
                            <button onClick={() => setEditingId(null)} style={{ ...S.btn('#555'), fontSize: 12 }}>✕</button>
                          </div>
                        ) : (
                          <span onClick={() => { setEditingId(u.id); setEditPlan(u.plan); }} style={S.badge(u.plan)}>{u.plan}</span>
                        )}
                      </td>
                      <td style={{ ...S.td, ...S.text2, textAlign: 'center' }}>{u.total_leads}</td>
                      <td style={{ ...S.td, ...S.text2, textAlign: 'center' }}>{u.total_messages}</td>
                      <td style={{ ...S.td, ...S.text2, textAlign: 'center' }}>{u.total_appointments}</td>
                      <td style={{ ...S.td, ...S.text2, fontSize: 11, whiteSpace: 'nowrap' }}>{u.created_at ? new Date(u.created_at).toLocaleDateString() : '—'}</td>
                      <td style={{ ...S.td, fontSize: 11, whiteSpace: 'nowrap', color: trialExpired ? '#f87171' : '#4ade80' }}>
                        {trialEnd ? trialEnd.toLocaleDateString() : '—'}{trialExpired ? ' ✗' : ''}
                      </td>
                      <td style={S.td}>
                        <div style={{ display: 'flex', gap: 5 }}>
                          <button onClick={() => handleExtendTrial(u.id)} style={{ ...S.btn('#4ade80'), whiteSpace: 'nowrap' }}>+14d</button>
                          <button onClick={() => handleDelete(u.id, u.name)} style={S.btn('#f87171')}>Del</button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
            {filtered.length === 0 && !loading && (
              <div style={{ padding: 32, textAlign: 'center', color: '#555', fontSize: 13 }}>No subscribers found</div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}